import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import api from './api/axiosInstance';
import base_url from './api/Config';
import { Circles } from 'react-loader-spinner';
import imageCompression from 'browser-image-compression';
import TopBar from './component/TopBar';

const documentFields = [
  { name: 'aadhaar_front', label: 'Aadhaar Card (Front)', typeId: '1' },
  { name: 'aadhaar_back', label: 'Aadhaar Card (Back)', typeId: '2' },
  { name: 'pan_card', label: 'PAN Card', typeId: '3' },
  { name: 'bank_statement', label: 'Bank Statement', typeId: '4' },
  { name: 'selfie', label: 'Selfie', typeId: '5' },
  { name: 'cheque_book', label: 'Cheque Book', typeId: '7' },
  { name: 'document_file', label: 'Supporting Document', typeId: '8' },
];

const DocumentUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const applicationId = params.get('applicationId');
  const customerId = params.get('customerId');

  const [documents, setDocuments] = useState({});
  const [previews, setPreviews] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [compressingFile, setCompressingFile] = useState(null);
  const [errorModal, setErrorModal] = useState(false);

  const handleFileChange = async (e, docName) => {
    const file = e.target.files[0];
    if (!file) return;
    setCompressingFile(docName);
    try {
      let finalFile = file;
      if (file.type.startsWith('image/')) {
        const blob = await imageCompression(file, { maxSizeMB: 0.5, maxWidthOrHeight: 1920, useWebWorker: true });
        const ext = file.name.split('.').pop();
        finalFile = new File([blob], `${docName}_${Date.now()}.${ext}`, { type: file.type });
      }
      setDocuments(prev => ({ ...prev, [docName]: finalFile }));
      setPreviews(prev => ({ ...prev, [docName]: URL.createObjectURL(finalFile) }));
    } catch {
      // compression failed, use original
      setDocuments(prev => ({ ...prev, [docName]: file }));
      setPreviews(prev => ({ ...prev, [docName]: URL.createObjectURL(file) }));
    } finally {
      setCompressingFile(null);
    }
  };

  const handleUploadAll = async () => {
    setIsLoading(true);
    try {
      for (const doc of documentFields) {
        const file = documents[doc.name];
        if (!file) continue;
        const formData = new FormData();
        formData.append('files', file);
        formData.append('documentTypeId', doc.typeId);
        formData.append('documentName', doc.name);
        formData.append('customerId', customerId);
        try {
          await api.post(`${base_url}/api/s3/reupload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch {
          // continue uploading remaining docs
        }
      }
      navigate(`/VideoUpload?applicationId=${applicationId}&customerId=${customerId}`);
    } catch {
      setErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app-page">
      <TopBar title="Document Upload" backPath={`/Reference?applicationId=${applicationId}&customerId=${customerId}`} />
      {isLoading && <div className="loader-overlay"><div className="spinner-border text-light" /></div>}

      <div className="app-content">
        {documentFields.map((doc) => (
          <div key={doc.name} className="app-card" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <p style={{ fontWeight: 600, fontSize: 14, color: '#1d2e77', marginBottom: 0 }}>
                {doc.label} <span className="field-star">*</span>
              </p>
              {previews[doc.name] && (
                <span style={{ fontSize: 11, color: '#11c232', fontWeight: 600 }}>Captured</span>
              )}
            </div>

            <input
              type="file"
              accept="image/*"
              capture="environment"
              id={`upload-${doc.name}`}
              style={{ display: 'none' }}
              onChange={e => handleFileChange(e, doc.name)}
            />

            {compressingFile === doc.name ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Circles height={28} width={28} color="#1d2e77" />
                <span style={{ fontSize: 13, color: '#666' }}>Compressing...</span>
              </div>
            ) : (
              <label
                htmlFor={`upload-${doc.name}`}
                style={{
                  display: 'inline-block', padding: '8px 16px', borderRadius: 8,
                  border: '1.5px solid #1d2e77', color: '#1d2e77', fontWeight: 600,
                  fontSize: 13, cursor: 'pointer',
                }}
              >
                {previews[doc.name] ? 'Replace' : 'Capture / Upload'}
              </label>
            )}

            {!compressingFile && previews[doc.name] && (
              <div style={{ marginTop: 10, textAlign: 'center' }}>
                <img src={previews[doc.name]} alt="Preview" style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 8, objectFit: 'cover' }} />
              </div>
            )}
          </div>
        ))}

        <button className="btn-primary-app" onClick={handleUploadAll} disabled={isLoading}>
          {isLoading ? 'Uploading...' : 'Upload & Continue'}
        </button>
      </div>

      <Modal show={errorModal} onHide={() => setErrorModal(false)} centered>
        <Modal.Body className="text-center p-4">
          <strong>Error uploading documents. Please try again.</strong>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-danger" onClick={() => setErrorModal(false)}>Close</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DocumentUpload;
